<template>
  <section class="timeline p-top-8 p-bot-7">
    <div class="timeline__header">
      <WidgetSliderSwiper
        class="timeline__carousel"
        :length="props.items.length"
        :options="{
          loop: true,
          slidesPerView: 1,
        }"
        :actualYear="actualYear"
        @init="onInit"
        @updateRealIndex="handleChange"
      >
        <template
          v-for="(yearObj, index) in props.items"
          :key="index"
          #[`slide-${index}`]
        >
          <div class="swiper-slide-content">
            <div class="timeline__carousel__left">
              <div
                v-show="yearObj.text"
                class="timeline__header__description title-base"
                v-html="yearObj.text"
              />
              <div
                v-show="yearObj.sectionTitle"
                class="timeline__header__year show-for-large"
                v-html="yearObj.sectionTitle"
              />
            </div>
            <div class="timeline__carousel__right">
              <div
                v-if="yearObj.portrait.length"
                class="timeline__header__image"
              >
                <WidgetPicture
                  :image="{
                    portrait: yearObj.portrait?.[0],
                  }"
                  :alt="yearObj.portrait?.[0]?.title"
                />
              </div>
              <div v-else class="timeline__header__placeholder"></div>
            </div>
          </div>
        </template>
      </WidgetSliderSwiper>
    </div>
    <div ref="timelineTrack" class="timeline__track m-top-8">
      <div
        ref="years"
        class="timeline__track__years"
        :style="{
          '-webkit-transform': 'translate3d(' + -offset + 'px,0,0)',
          transform: 'translate3d(' + -offset + 'px,0,0)',
        }"
        @mouseenter="hovering = true"
        @mouseleave="hovering = false"
      >
        <div
          v-for="(year, yearIndex) in yearsArray"
          :key="yearIndex"
          class="timeline__track__years__year"
          :class="{
            '--selectable': selectableYears.includes(year),
            '--active': actualYear === year,
          }"
          @click="setYear(year)"
        >
          <div class="timeline__track__years__year__dot"></div>
          <div class="timeline__track__years__year__line" />
          <div class="timeline__track__years__year__dots-container">
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
            <div class="dot"></div>
          </div>
          <span class="timeline__track__years__year__description">{{
            year
          }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
const props = defineProps({
  items: {
    type: Array,
  },
})

const actualYear = ref(null)
const actualIndex = ref(null)
const docWidth = ref(null)
const slidesWidth = ref(null)
const rangeX = ref(null)
const years = ref(null)
const offset = ref(0)
const hovering = ref(false)
const timelineCarousel = ref(null)
const timelineTrack = ref(null)

const firstYear = 1980
const lastYear = new Date().getFullYear()

const yearsArray = computed(() => {
  const years = []
  let startYear = firstYear
  while (startYear <= lastYear) {
    years.push(startYear.toString())
    startYear++
  }
  return years
})

const selectableYears = computed(() => {
  return props.items.map((yearObj) => {
    return yearObj.sectionTitle.replace(/<\/?[^>]+(>|$)/g, '')
  })
})

watch(actualIndex, (val) => {
  const selectedYear = selectableYears.value[val]
  actualYear.value = selectedYear

  setTimeout(() => {
    const selectedYearEl = years.value.querySelector(
      '.timeline__track__years__year.--active',
    )
    if (!selectedYearEl || hovering.value) return
    const elOffsetLeft = selectedYearEl.offsetLeft
    let updatedOffset = elOffsetLeft - docWidth.value / 2

    if (docWidth.value >= 1000) {
      if (updatedOffset > rangeX.value) updatedOffset = rangeX.value
      if (updatedOffset < 0) updatedOffset = 0
      offset.value = updatedOffset
    } else {
      const track = timelineTrack.value
      track.scrollLeft = updatedOffset
    }
  }, 300)
})

const initTrackAnimation = () => {
  docWidth.value = document.body.clientWidth
  years.value = years.value
  slidesWidth.value = years.value.offsetWidth
  rangeX.value = slidesWidth.value - docWidth.value

  if (docWidth.value < 1000) {
    offset.value = 0
    hovering.value = false
  }
}

const setYear = (newYear) => {
  actualYear.value = newYear
  const selectedYearIndex = selectableYears.value.findIndex(
    (year) => year === newYear,
  )
  if (timelineCarousel.value) {
    timelineCarousel.value.slideTo(selectedYearIndex)
  } else {
    console.log('Swiper instance not found')
  }
}

const onInit = (swiper) => {
  timelineCarousel.value = swiper
}

const handleChange = (index) => {
  actualIndex.value = index
  actualYear.value = selectableYears.value[index]
}

onMounted(() => {
  actualIndex.value = 0

  initTrackAnimation()

  const yearsTrackEl = years.value
  const trackEl = timelineTrack.value

  yearsTrackEl.addEventListener('mousemove', (e) => {
    const mouseX = e.pageX
    const percentMouse = (mouseX * 100) / docWidth.value
    let offsetValue =
      (percentMouse / 100) * slidesWidth.value -
      (percentMouse / 200) * docWidth.value

    if (offsetValue > rangeX.value) offsetValue = rangeX.value
    if (offsetValue < 0) offsetValue = 0
    offset.value = docWidth.value >= 1000 ? offsetValue : 0
  })

  let startX = 0
  let scrollLeft = 0

  if (docWidth.value < 1000 && trackEl) {
    trackEl.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].pageX
        scrollLeft = trackEl.scrollLeft
      },
      { passive: true },
    )

    trackEl.addEventListener(
      'touchmove',
      (e) => {
        if (!startX) return
        const x = e.touches[0].pageX
        const walk = (startX - x) * 1.5
        trackEl.scrollLeft = scrollLeft + walk
        e.preventDefault()
      },
      { passive: false },
    )

    trackEl.addEventListener(
      'touchend',
      () => {
        startX = 0
      },
      { passive: true },
    )
  }

  window.addEventListener('resize', initTrackAnimation)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', initTrackAnimation)
})
</script>

<style lang="scss">
@import './style.scss';
</style>
